
import { useState, useEffect, useRef } from 'react';
import ACMegaDropdown from './ACMegaDropdown';
import HeatingMegaDropdown from './HeatingMegaDropdown';

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isACDropdownOpen, setIsACDropdownOpen] = useState(false);
  const [isHeatingDropdownOpen, setIsHeatingDropdownOpen] = useState(false);
  const [isMobileACOpen, setIsMobileACOpen] = useState(false);
  const [isMobileHeatingOpen, setIsMobileHeatingOpen] = useState(false);
  const [mobileActiveSubCat, setMobileActiveSubCat] = useState<'ac-services' | 'indoor-air-quality'>('ac-services');
  const [mobileHeatingActiveSubCat, setMobileHeatingActiveSubCat] = useState<'heating-services' | 'indoor-air-quality'>('heating-services');
  const dropdownTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const heatingDropdownTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleMouseEnter = () => {
    if (dropdownTimeoutRef.current) {
      clearTimeout(dropdownTimeoutRef.current);
      dropdownTimeoutRef.current = null;
    }
    setIsHeatingDropdownOpen(false);
    setIsACDropdownOpen(true);
  };

  const handleMouseLeave = () => {
    dropdownTimeoutRef.current = setTimeout(() => {
      setIsACDropdownOpen(false);
    }, 200);
  };

  const handleHeatingMouseEnter = () => {
    if (heatingDropdownTimeoutRef.current) {
      clearTimeout(heatingDropdownTimeoutRef.current);
      heatingDropdownTimeoutRef.current = null;
    }
    setIsACDropdownOpen(false);
    setIsHeatingDropdownOpen(true);
  };

  const handleHeatingMouseLeave = () => {
    heatingDropdownTimeoutRef.current = setTimeout(() => {
      setIsHeatingDropdownOpen(false);
    }, 200);
  };

  const acServicesItemsMobile = [
    { title: 'AC Installation', href: '#ac-installation' },
    { title: 'AC Maintenance', href: '#ac-maintenance' },
    { title: 'AC Repair', href: '#ac-repair' },
    { title: 'Commercial HVAC', href: '#commercial-hvac' },
    { title: 'HVAC Equipment', href: '#hvac-equipment' },
  ];

  const indoorAirQualityItemsMobile = [
    { title: 'Duct Cleaning', href: '#duct-cleaning' },
    { title: 'Insulation', href: '#insulation' },
    { title: 'Air Duct Sealing', href: '#air-duct-sealing' },
    { title: 'Indoor Air & Thermostat Equipment', href: '#thermostat-equipment' },
    { title: 'Whole Home Air Purifiers', href: '#air-purifiers' },
  ];

  const heatingServicesItemsMobile = [
    { title: 'Heating Installation', href: '#heating-installation' },
    { title: 'Heating Maintenance', href: '#heating-maintenance' },
    { title: 'Heating Repair', href: '#heating-repair' },
    { title: 'Commercial HVAC', href: '#commercial-hvac-heating' },
    { title: 'HVAC Equipment', href: '#hvac-equipment-heating' },
  ];

  const heatingIndoorAirQualityItemsMobile = [
    { title: 'Indoor Air Quality', href: '#indoor-air-quality-heating' },
    { title: 'Duct Cleaning', href: '#duct-cleaning-heating' },
    { title: 'Insulation', href: '#insulation-heating' },
    { title: 'Air Duct Sealing', href: '#air-duct-sealing-heating' },
    { title: 'Indoor Air & Thermostat Equipment', href: '#thermostat-equipment-heating' },
    { title: 'Whole Home Air Purifiers', href: '#air-purifiers-heating' },
  ];

  return (
    <nav
      className={`fixed w-full z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-white'}`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          <div className="flex items-center">
            <a href="/">
              <h1>
                <img
                  src="https://static.readdy.ai/image/73df10f1d2f13e308059e9f42ef7d721/7da722d1075287828d0f792d894e39bf.jpeg"
                  alt="Voltis - Plumbing, Heating, AC & Electrical Services in Florida"
                  title="Voltis Florida Home Services"
                  className="h-14 w-auto object-contain"
                />
              </h1>
            </a>
          </div>

          <div className="hidden lg:flex items-center space-x-8">
            {/* Air Conditioning with Mega Dropdown */}
            <div
              className="relative"
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              <a
                href="#services"
                className={`text-sm font-medium transition-colors whitespace-nowrap flex items-center gap-1 cursor-pointer ${
                  isACDropdownOpen ? 'text-[#B22234]' : 'text-[#1B3A6B] hover:text-[#B22234]'
                }`}
              >
                Air Conditioning
                <i className={`ri-arrow-down-s-line text-xs transition-transform duration-200 ${isACDropdownOpen ? 'rotate-180' : ''}`}></i>
              </a>
            </div>

            {/* Heating with Mega Dropdown */}
            <div
              className="relative"
              onMouseEnter={handleHeatingMouseEnter}
              onMouseLeave={handleHeatingMouseLeave}
            >
              <a
                href="#services"
                className={`text-sm font-medium transition-colors whitespace-nowrap flex items-center gap-1 cursor-pointer ${
                  isHeatingDropdownOpen ? 'text-[#B22234]' : 'text-[#1B3A6B] hover:text-[#B22234]'
                }`}
              >
                Heating
                <i className={`ri-arrow-down-s-line text-xs transition-transform duration-200 ${isHeatingDropdownOpen ? 'rotate-180' : ''}`}></i>
              </a>
            </div>

            <a href="#services" className="text-sm font-medium text-[#1B3A6B] hover:text-[#B22234] transition-colors whitespace-nowrap">
              Plumbing
            </a>
            <a href="#services" className="text-sm font-medium text-[#1B3A6B] hover:text-[#B22234] transition-colors whitespace-nowrap">
              Water Quality
            </a>
            <a href="#services" className="text-sm font-medium text-[#1B3A6B] hover:text-[#B22234] transition-colors whitespace-nowrap">
              Electrical
            </a>
          </div>

          <div className="hidden lg:flex items-center space-x-4">
            <a
              href="tel:+18005551234"
              className="flex items-center space-x-2 text-sm font-semibold text-[#B22234] hover:text-[#8B1A29] transition-colors whitespace-nowrap"
            >
              <i className="ri-phone-fill text-lg"></i>
              <span>(800) 555-1234</span>
            </a>
            <a
              href="#contact"
              className="bg-[#B22234] text-white px-6 py-2.5 rounded-lg text-sm font-semibold hover:bg-[#8B1A29] transition-colors shadow-lg whitespace-nowrap"
            >
              Schedule Service
            </a>
          </div>

          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-2 cursor-pointer"
            aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
          >
            <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl text-[#1B3A6B]`}></i>
          </button>
        </div>
      </div>

      {/* Desktop AC Mega Dropdown */}
      {isACDropdownOpen && (
        <div
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <ACMegaDropdown />
        </div>
      )}

      {/* Desktop Heating Mega Dropdown */}
      {isHeatingDropdownOpen && (
        <div
          onMouseEnter={handleHeatingMouseEnter}
          onMouseLeave={handleHeatingMouseLeave}
        >
          <HeatingMegaDropdown />
        </div>
      )}

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="lg:hidden bg-white border-t max-h-[80vh] overflow-y-auto">
          <div className="px-4 py-4 space-y-1">
            {/* Air Conditioning Accordion */}
            <div>
              <button
                onClick={() => setIsMobileACOpen(!isMobileACOpen)}
                className="flex items-center justify-between w-full text-sm font-medium text-[#1B3A6B] hover:text-[#B22234] py-2.5 cursor-pointer whitespace-nowrap"
              >
                <span>Air Conditioning</span>
                <i className={`ri-arrow-down-s-line transition-transform duration-200 ${isMobileACOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {isMobileACOpen && (
                <div className="pl-4 pb-2">
                  <div className="flex gap-2 mb-3">
                    <button
                      onClick={() => setMobileActiveSubCat('ac-services')}
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
                        mobileActiveSubCat === 'ac-services'
                          ? 'bg-[#B22234] text-white'
                          : 'bg-[#1B3A6B]/5 text-[#1B3A6B]'
                      }`}
                    >
                      AC Services
                    </button>
                    <button
                      onClick={() => setMobileActiveSubCat('indoor-air-quality')}
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
                        mobileActiveSubCat === 'indoor-air-quality'
                          ? 'bg-[#B22234] text-white'
                          : 'bg-[#1B3A6B]/5 text-[#1B3A6B]'
                      }`}
                    >
                      Indoor Air Quality
                    </button>
                  </div>
                  <div className="space-y-1">
                    {(mobileActiveSubCat === 'ac-services' ? acServicesItemsMobile : indoorAirQualityItemsMobile).map((item) => (
                      <a
                        key={item.title}
                        href={item.href}
                        className="block text-xs text-[#1B3A6B]/70 hover:text-[#B22234] py-1.5 pl-2 border-l-2 border-transparent hover:border-[#B22234] transition-all"
                      >
                        {item.title}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Heating Accordion */}
            <div>
              <button
                onClick={() => setIsMobileHeatingOpen(!isMobileHeatingOpen)}
                className="flex items-center justify-between w-full text-sm font-medium text-[#1B3A6B] hover:text-[#B22234] py-2.5 cursor-pointer whitespace-nowrap"
              >
                <span>Heating</span>
                <i className={`ri-arrow-down-s-line transition-transform duration-200 ${isMobileHeatingOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {isMobileHeatingOpen && (
                <div className="pl-4 pb-2">
                  <div className="flex gap-2 mb-3">
                    <button
                      onClick={() => setMobileHeatingActiveSubCat('heating-services')}
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
                        mobileHeatingActiveSubCat === 'heating-services'
                          ? 'bg-[#B22234] text-white'
                          : 'bg-[#1B3A6B]/5 text-[#1B3A6B]'
                      }`}
                    >
                      Heating Services
                    </button>
                    <button
                      onClick={() => setMobileHeatingActiveSubCat('indoor-air-quality')}
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
                        mobileHeatingActiveSubCat === 'indoor-air-quality'
                          ? 'bg-[#B22234] text-white'
                          : 'bg-[#1B3A6B]/5 text-[#1B3A6B]'
                      }`}
                    >
                      Indoor Air Quality
                    </button>
                  </div>
                  <div className="space-y-1">
                    {(mobileHeatingActiveSubCat === 'heating-services' ? heatingServicesItemsMobile : heatingIndoorAirQualityItemsMobile).map((item) => (
                      <a
                        key={item.title}
                        href={item.href}
                        className="block text-xs text-[#1B3A6B]/70 hover:text-[#B22234] py-1.5 pl-2 border-l-2 border-transparent hover:border-[#B22234] transition-all"
                      >
                        {item.title}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <a href="#services" className="block text-sm font-medium text-[#1B3A6B] hover:text-[#B22234] py-2.5 whitespace-nowrap">
              Plumbing
            </a>
            <a href="#services" className="block text-sm font-medium text-[#1B3A6B] hover:text-[#B22234] py-2.5 whitespace-nowrap">
              Water Quality
            </a>
            <a href="#services" className="block text-sm font-medium text-[#1B3A6B] hover:text-[#B22234] py-2.5 whitespace-nowrap">
              Electrical
            </a>

            <div className="pt-3 border-t border-gray-100 mt-2 space-y-3">
              <a
                href="tel:+18005551234"
                className="flex items-center space-x-2 text-sm font-semibold text-[#B22234] py-2 whitespace-nowrap"
              >
                <i className="ri-phone-fill text-lg"></i>
                <span>(800) 555-1234</span>
              </a>
              <a
                href="#contact"
                className="block bg-[#B22234] text-white px-6 py-2.5 rounded-lg text-sm font-semibold text-center hover:bg-[#8B1A29] transition-colors whitespace-nowrap"
              >
                Schedule Service
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}
